import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface EventDetails {
  id: string;
  title: string;
  shortDescription: string;
  longDescription: string;
  prerequisite: string;
  expectedOutcome: string;
  visibility: string;
  registrationType: string;
  blocking: boolean;
  mode: string;
  approvalRequired: boolean;
  jobLevel: string;
  proficiency: string;
  searchTags: {
    defaultTag: string[];
    eventCategory: string[];
    eventSubCategory: string[];
    eventUsage: string[];
    eventRegion: string[];
    eventDomain: string[];
  };
  advancetags: {
    category: string;
    subcategory: string;
    usage: string;
    domain: string;
    region: string;
    nominationPercentage: number;
  };
  minCapacity: number;
  maxCapacity: number;
  waitListPercent: number;
  nominationStartDate: string;
  nominationEndDate: string;
  startDate: string;
  endDate: string;
  zoneId: string;
  language: string;
  location: string;
  org: string;
  status: string;
  publishedAt: string | null;
  publishedBy: string | null;
  clonedId: string | null;
  templateId: string | null;
  cohortId: string | null;
  privateUserGroup: string[];
  eventType: string;
  skills: string[];
  learningContent: any[];
  socialInteractions: any[];
  branding: {
    thumbnail: string[];
    promo: string[];
    banner: string[];
  };
  sessionList: any[];
  expense: any[];
  config: {
    reviewRequired: boolean;
    reminderConfig: {
      duration: number;
      trigger: string;
    };
    speakerCalInvite: boolean;
    candidateCalInvite: boolean;
    speakerNotify: boolean;
    candidateNotify: boolean;
    allowNonEnrolledForAttendance: boolean;
    pastEvent: boolean;
  };
  stakeholder: {
    owner: string[];
    observer: string[];
    reviewer: string[];
    nominationApprover: string[];
  };
  nominationEligibilty: {
    jobLevel: boolean;
    proficiency: boolean;
    role: boolean;
    function: boolean;
  };
  attendancePassPercentage: number;
  registrationCost: {
    amount: number;
    currency: string;
  };
  role: string[];
  function: string[];
}

export interface EventDetailsState extends EventDetails {
  isLoading: boolean;
  error: string | null;
}

const initialState: EventDetailsState = {
  id: "",
  title: "",
  shortDescription: "",
  longDescription: "",
  prerequisite: "",
  expectedOutcome: "",
  visibility: "eventVisibilityType1",
  registrationType: "",
  blocking: false,
  mode: "",
  approvalRequired: false,
  jobLevel: "",
  proficiency: "",
  searchTags: {
    defaultTag: [],
    eventCategory: [],
    eventSubCategory: [],
    eventUsage: [],
    eventRegion: [],
    eventDomain: []
  },
  advancetags: {
    category: "",
    subcategory: "",
    usage: "",
    domain: "",
    region: "",
    nominationPercentage: 0
  },
  minCapacity: 0,
  maxCapacity: 0,
  waitListPercent: 0,
  nominationStartDate: "",
  nominationEndDate: "",
  startDate: "",
  endDate: "",
  zoneId: "",
  language: "",
  location: "",
  org: "",
  status: "",
  publishedAt: null,
  publishedBy: null,
  clonedId: null,
  templateId: null,
  cohortId: null,
  privateUserGroup: [],
  eventType: "",
  skills: [],
  learningContent: [],
  socialInteractions: [],
  branding: {
    thumbnail: [],
    promo: [],
    banner: []
  },
  sessionList: [],
  expense: [],
  config: {
    reviewRequired: false,
    reminderConfig: {
      duration: 0,
      trigger: ""
    },
    speakerCalInvite: false,
    candidateCalInvite: false,
    speakerNotify: false,
    candidateNotify: false,
    allowNonEnrolledForAttendance: false,
    pastEvent: false
  },
  stakeholder: {
    owner: [],
    observer: [],
    reviewer: [],
    nominationApprover: []
  },
  nominationEligibilty: {
    jobLevel: false,
    proficiency: false,
    role: false,
    function: false
  },
  attendancePassPercentage: 0,
  registrationCost: {
    amount: 0,
    currency: ""
  },
  role: [],
  function: [],
  isLoading: false,
  error: null
};

const eventDetailsSlice = createSlice({
  name: "eventDetails",
  initialState,
  reducers: {
    setEventDetails: (
      state,
      action: PayloadAction<Partial<EventDetailsState>>
    ) => {
      return {
        ...state,
        ...action.payload
      };
    },
    clearEventDetails: (state) => {
      return {
        ...initialState,
        isLoading: false,
        error: null
      };
    },
    setAdvancedTags: (
      state,
      action: PayloadAction<Partial<EventDetails["advancetags"]>>
    ) => {
      return {
        ...state,
        advancetags: {
          ...state.advancetags,
          ...action.payload
        }
      };
    }
  }
});

export const { setEventDetails, clearEventDetails, setAdvancedTags } =
  eventDetailsSlice.actions;

export default eventDetailsSlice.reducer;
